import { forwardRef, useState } from 'react';

/**
 * Segmented one-time-code field.
 *
 * ONE real input, transparent, layered over N drawn boxes — not N inputs.
 * Six inputs is the obvious build and the wrong one: it breaks paste, breaks
 * `autocomplete="one-time-code"` autofill, confuses screen readers (six
 * unlabelled fields), and needs per-box focus juggling, which is the same class
 * of bug that made this dialog's code field unusable in the first place.
 *
 * With a single input, typing, pasting, backspacing, autofill and selection all
 * behave natively; the boxes are pure presentation. It also keeps one ref, so
 * Modal's initialFocusRef still has a single thing to focus.
 *
 * The caret is hidden and a highlighted box stands in for it, so the visual
 * position always matches `value.length` rather than a real caret the user
 * could move into the middle of the string.
 */
const CodeInput = forwardRef(function CodeInput({
  value = '',
  onChange,
  onKeyDown,
  length = 6,
  disabled = false,
  invalid = false,
  id,
  t,
  ariaDescribedBy,
}, ref) {
  const [focused, setFocused] = useState(false);
  const chars = String(value).slice(0, length).split('');

  return (
    <div className="relative mt-1">
      <input
        id={id}
        ref={ref}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={() => { setFocused(true); }}
        onBlur={() => { setFocused(false); }}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        aria-label={`${length}-digit code`}
        aria-invalid={invalid ? 'true' : undefined}
        aria-describedby={ariaDescribedBy}
        // Transparent, not hidden: it must stay focusable, tabbable and visible
        // to assistive tech. Caret is hidden because a box stands in for it.
        className="absolute inset-0 z-10 h-full w-full cursor-text rounded-md bg-transparent text-transparent outline-none"
        style={{ caretColor: 'transparent' }}
      />

      <div aria-hidden="true" className="flex gap-2">
        {Array.from({ length }).map((_, i) => {
          const char = chars[i];
          // The "next" box is where the caret would be. When the field is full
          // the last box stays lit rather than pointing past the end.
          const isNext = focused && (i === Math.min(chars.length, length - 1));
          const active = isNext && chars.length < length;
          const filled = Boolean(char);
          return (
            <div
              // Index is the identity here: these are fixed positions, not a
              // reorderable list.
              key={i}
              className={`flex h-12 flex-1 items-center justify-center rounded-md border text-[20px] transition-colors ${disabled ? 'opacity-55' : ''}`}
              style={{
                backgroundColor: t.inputBg,
                color: t.inputText,
                fontFamily: t.fonts.mono,
                borderColor: invalid
                  ? t.danger
                  : active
                    ? t.heading
                    : filled
                      ? t.controlBorder
                      : t.inputBorder,
                // A subtle ring on the active box replaces the caret.
                boxShadow: active ? `0 0 0 1px ${t.heading}` : 'none',
              }}
            >
              {char || ''}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CodeInput;
